import type { Vector3 } from 'three';
import { useSketchStore } from '../../state/useSketchStore';
import { useAppStore } from '../../state/store';
import { gumballOwnsPointer } from '../../state/useGumballStore';
import {
    faceSelectionId, edgeSelectionId, vertexSelectionId, looseEdgeSelectionId,
    parseLooseEdgeSelectionId,
} from '../../core/sketch/types';
import { edgeChainKeys, solidVertexPoints } from '../../core/sketch/topology';

/**
 * Sketch tool event bus.
 *
 * Unlike the CAD ToolScope (whose scopes wrap <group> handlers around
 * children), Sketch's face meshes and the event plane are SEPARATE layers —
 * faces must not swallow each other's picks, and the empty-plane click must
 * clear the selection. So every hit (face / plane) is emitted onto this bus
 * and the ACTIVE tool hook (mounted by SketchToolScope) owns the response.
 * With no tool registered the bus applies the default select/clear contract.
 */

export interface SketchHit {
    /** 'face' = a modeled face pick; 'plane' = empty space / event plane;
     *  'edge' = a loose edge pick (edgeId set); 'annotation' = a dimension
     *  or text label pick (annotationId set); 'solidEdge' = a shared solid
     *  border pick (solidId + edgeKey set); 'solidVertex' = a welded corner
     *  pick (solidId + vertexKey set); 'looseVertex' = a loose edge endpoint
     *  pick (edgeId + end set). */
    kind: 'face' | 'plane' | 'edge' | 'annotation' | 'solidEdge' | 'solidVertex' | 'looseVertex';
    solidId?: string;
    faceId?: string;
    /** Loose edge id when kind === 'edge' or 'looseVertex'. */
    edgeId?: string;
    /** Grabbed endpoint when kind === 'looseVertex'. */
    end?: 'a' | 'b';
    /** Topology key of the shared solid edge when kind === 'solidEdge'. */
    edgeKey?: string;
    /** Weld key of the solid corner when kind === 'solidVertex'. */
    vertexKey?: string;
    /** Annotation id when kind === 'annotation'. */
    annotationId?: string;
    /** World-space hit point (on the face plane or the Z=0 event plane). */
    point: Vector3;
    shiftKey: boolean;
    ctrlKey: boolean;
    altKey: boolean;
    button: number;
    /** Native pointer position in client px, from the exact event that
     *  produced this hit (event.nativeEvent.clientX/clientY). */
    pointerPx?: { x: number; y: number };
}

export interface SketchToolHandlers {
    onDown?: (hit: SketchHit) => void;
    onMove?: (hit: SketchHit) => void;
    /** Escape / arrow keys reach the tool through here. */
    onKey?: (key: string, event: KeyboardEvent) => void;
}

let active: SketchToolHandlers | null = null;

export function registerSketchTool(handlers: SketchToolHandlers | null): void {
    active = handlers;
}

/**
 * Select-mode face-drag gesture (Shift+drag = Push/Pull), plugged in by
 * SketchModifyTools so the bus stays UI-free. It only ever owns the pointer
 * while NO tool is registered — an active tool keeps its Shift semantics.
 */
export interface SketchFaceDragController {
    /** Arm the gesture on a face hit; false falls through to face select. */
    begin: (hit: SketchHit) => boolean;
    /** Live drag update (face or plane hits). */
    drag: (hit: SketchHit) => void;
    /** Release commits; a cancel rolls back without touching the model. */
    end: (commit: boolean) => void;
    readonly dragging: boolean;
}

/**
 * Select-mode edge-drag gesture (SketchUp's primary interaction: press an
 * edge and drag to move it), plugged in by SketchModifyTools so the bus
 * stays UI-free. It only ever owns the pointer while NO tool is registered;
 * a plain click without a drag falls through on release as chain select.
 */
export interface SketchEdgeDragController {
    /** Arm the gesture on a solid-edge hit; false falls through to select. */
    begin: (hit: SketchHit) => boolean;
    /** Live drag update (edge, face or plane hits). */
    drag: (hit: SketchHit) => void;
    /** Release commits; a cancel rolls back without touching the model. */
    end: (commit: boolean) => void;
    readonly dragging: boolean;
}

/**
 * Select-mode vertex-drag gesture (SketchUp corner move: press a welded
 * corner and drag — the corner and every attached face follow), plugged in
 * by SketchModifyTools. It only ever owns the pointer while NO tool is
 * registered; a plain click without a drag falls through on release as
 * vertex select.
 */
export interface SketchVertexDragController {
    /** Arm the gesture on a solid-vertex hit; false falls through to select. */
    begin: (hit: SketchHit) => boolean;
    /** Live drag update (vertex, edge, face or plane hits). */
    drag: (hit: SketchHit) => void;
    /** Release commits; a cancel rolls back without touching the model. */
    end: (commit: boolean) => void;
    readonly dragging: boolean;
}

/**
 * Select-mode loose-edge drag gesture (SketchUp's line move: press a drawn
 * segment and drag — the segment, its two endpoint weld clusters and the
 * pre-selection follow), plugged in by SketchModifyTools. It only ever owns
 * the pointer while NO tool is registered; a plain click without a drag
 * falls through on release as loose-edge select.
 */
export interface SketchLooseEdgeDragController {
    /** Arm the gesture on a loose-edge hit; false falls through to select. */
    begin: (hit: SketchHit) => boolean;
    /** Live drag update (loose-edge, face or plane hits). */
    drag: (hit: SketchHit) => void;
    /** Release commits; a cancel rolls back without touching the model. */
    end: (commit: boolean) => void;
    readonly dragging: boolean;
}

/**
 * Select-mode loose-endpoint drag gesture (SketchUp endpoint stretch: press
 * the start/end of a drawn segment and drag — the grabbed endpoint and every
 * welded endpoint follow, the far end stays), plugged in by
 * SketchModifyTools. A plain click without a drag falls through on release
 * as loose-edge select.
 */
export interface SketchLooseVertexDragController {
    /** Arm the gesture on a loose-endpoint hit; false falls through to select. */
    begin: (hit: SketchHit) => boolean;
    /** Live drag update (endpoint, face or plane hits). */
    drag: (hit: SketchHit) => void;
    /** Release commits; a cancel rolls back without touching the model. */
    end: (commit: boolean) => void;
    readonly dragging: boolean;
}

/**
 * Select-mode annotation drag gesture (SketchUp annotation move: press a
 * dimension's dim line or a text label and drag it aside), plugged in by
 * SketchModifyTools. Dimensions reposition their dim line (measured points
 * stay pinned); labels ride whole (anchor + leader). A clean click without a
 * drag is swallowed — annotations have no click-select contract yet.
 */
export interface SketchAnnotationDragController {
    /** Arm the gesture on an annotation hit; false falls through to the clear. */
    begin: (hit: SketchHit) => boolean;
    /** Live drag update (annotation or plane hits). */
    drag: (hit: SketchHit) => void;
    /** Release commits; a cancel rolls back without touching the model. */
    end: (commit: boolean) => void;
    readonly dragging: boolean;
}

/**
 * Select-mode marquee gesture (window/crossing box select), plugged in by
 * SketchSelectMarquee. It arms on a press on EMPTY SPACE (the event plane)
 * while NO tool is registered — a press that starts on geometry keeps the
 * click/drag gestures. The controller tracks the pointer through its own
 * window-level listeners (the rubber rect must keep updating over faces,
 * foreign references and even outside the canvas), so the bus only needs
 * the arming hook: `begin` returning true means the empty-space press is
 * now a marquee candidate and the default clear must stand down — the
 * controller itself performs the clean-click clear when released undragged.
 */
export interface SketchMarqueeController {
    /** Arm on an empty-space press; false falls through to the clear. */
    begin: (hit: SketchHit) => boolean;
    /** Stand a live gesture down without committing: an edit drag
     *  (face/edge/vertex) took the pointer while a marquee candidate was
     *  still armed. One pointer gesture at a time. */
    cancel?: () => void;
}

let faceDragController: SketchFaceDragController | null = null;

export function setSketchFaceDragController(controller: SketchFaceDragController | null): void {
    faceDragController = controller;
}

let edgeDragController: SketchEdgeDragController | null = null;

export function setSketchEdgeDragController(controller: SketchEdgeDragController | null): void {
    edgeDragController = controller;
}

let vertexDragController: SketchVertexDragController | null = null;

export function setSketchVertexDragController(controller: SketchVertexDragController | null): void {
    vertexDragController = controller;
}

let looseEdgeDragController: SketchLooseEdgeDragController | null = null;

export function setSketchLooseEdgeDragController(controller: SketchLooseEdgeDragController | null): void {
    looseEdgeDragController = controller;
}

let looseVertexDragController: SketchLooseVertexDragController | null = null;

export function setSketchLooseVertexDragController(controller: SketchLooseVertexDragController | null): void {
    looseVertexDragController = controller;
}

let annotationDragController: SketchAnnotationDragController | null = null;

export function setSketchAnnotationDragController(controller: SketchAnnotationDragController | null): void {
    annotationDragController = controller;
}

let marqueeController: SketchMarqueeController | null = null;

export function setSketchMarqueeController(controller: SketchMarqueeController | null): void {
    marqueeController = controller;
}

/** True while a select-mode edit drag (face/edge/vertex/loose/annotation)
 *  owns the pointer: the marquee and the empty-space clear must stand down
 *  so an edit gesture never runs a box selection underneath it. */
export function sketchDragOwnsPointer(): boolean {
    return !!(faceDragController?.dragging ||
        edgeDragController?.dragging ||
        vertexDragController?.dragging ||
        looseEdgeDragController?.dragging ||
        looseVertexDragController?.dragging ||
        annotationDragController?.dragging);
}

/** Default face pick: select the face (sketch store) + the solid (app store). */
function defaultFaceSelect(hit: SketchHit): void {
    if (!hit.solidId || !hit.faceId) return;
    const solid = useSketchStore.getState().solids[hit.solidId];
    if (!solid || solid.locked) return;
    const composite = faceSelectionId(hit.solidId, hit.faceId);
    useSketchStore.getState().select([composite]);
    useAppStore.getState().select([hit.solidId]);
}

/**
 * Default solid-edge pick: select the edge's whole CHAIN (`edgeChainKeys` —
 * Sketch's Curve entity: segments joined through 2-valent corners move as
 * one piece) as `solidId:edge:<key>` composites in the sketch store, plus
 * the owning solid in the app store. Shift/Ctrl toggles the chain in (or,
 * when fully selected, out of) the selection.
 */
function defaultSolidEdgeSelect(hit: SketchHit): void {
    if (!hit.solidId || !hit.edgeKey) return;
    const store = useSketchStore.getState();
    const solid = store.solids[hit.solidId];
    if (!solid || solid.locked) return;
    const chainKeys = edgeChainKeys(solid, hit.edgeKey);
    if (chainKeys.length === 0) return;
    const chainIds = chainKeys.map((key) => edgeSelectionId(hit.solidId!, key));
    const allSelected = chainIds.every((id) => store.selectedIds.includes(id));
    if (hit.shiftKey || hit.ctrlKey) {
        const next = allSelected
            ? store.selectedIds.filter((id) => !chainIds.includes(id))
            : [...store.selectedIds, ...chainIds.filter((id) => !store.selectedIds.includes(id))];
        store.select(next);
    } else {
        store.select(chainIds);
    }
    useAppStore.getState().select([hit.solidId]);
}

/**
 * Default solid-vertex pick: select the corner as a `solidId:vertex:<key>`
 * composite in the sketch store plus the owning solid in the app store.
 * Shift/Ctrl toggles the corner in (or out of) the selection.
 */
function defaultSolidVertexSelect(hit: SketchHit): void {
    if (!hit.solidId || !hit.vertexKey) return;
    const store = useSketchStore.getState();
    const solid = store.solids[hit.solidId];
    if (!solid || solid.locked) return;
    if (solidVertexPoints(solid, hit.vertexKey).length === 0) return;
    const composite = vertexSelectionId(hit.solidId, hit.vertexKey);
    const selected = store.selectedIds.includes(composite);
    if (hit.shiftKey || hit.ctrlKey) {
        store.select(selected
            ? store.selectedIds.filter((id) => id !== composite)
            : [...store.selectedIds, composite]);
    } else {
        store.select([composite]);
    }
    useAppStore.getState().select([hit.solidId]);
}

/** Default empty-space click: clear both selections. */
function defaultClear(): void {
    useSketchStore.getState().select([]);
    useAppStore.getState().select([]);
}

/**
 * Default loose-edge pick: select the drawn segment as a `looseEdge:<id>`
 * composite in the sketch store. Loose edges are not app-store entities, so
 * the app mirror empties (the gizmo has no target to sit on). Shift/Ctrl
 * toggles the edge in (or, when selected, out of) the selection.
 */
function defaultLooseEdgeSelect(hit: SketchHit): void {
    if (!hit.edgeId) return;
    const store = useSketchStore.getState();
    if (!store.edges[hit.edgeId]) return;
    const composite = looseEdgeSelectionId(hit.edgeId);
    const selected = store.selectedIds.includes(composite);
    if (hit.shiftKey || hit.ctrlKey) {
        store.select(selected
            ? store.selectedIds.filter((id) => id !== composite)
            : [...store.selectedIds, composite]);
    } else {
        store.select([composite]);
    }
    useAppStore.getState().select([]);
}

export function emitSketchDown(hit: SketchHit): void {
    // A press on a gizmo handle (or mid-gizmo-drag) belongs to the
    // manipulator: the select/clear contract and the drag gestures stand
    // down, otherwise grabbing an arrow over empty space would clear the
    // selection and kill the gizmo mid-grab.
    if (gumballOwnsPointer()) return;
    if (active?.onDown) {
        active.onDown(hit);
        return;
    }
    // Select-mode Shift+drag on a face starts the push/pull gesture; a
    // shift+click without a drag falls through on release as a face select.
    if (hit.kind === 'face' && hit.shiftKey && faceDragController?.begin(hit)) {
        marqueeController?.cancel?.();
        return;
    }
    // Select-mode press-drag on a welded corner moves it (SketchUp corner
    // move); a click without a drag falls through on release as select.
    if (hit.kind === 'solidVertex' && vertexDragController?.begin(hit)) {
        marqueeController?.cancel?.();
        return;
    }
    // Select-mode press-drag on a drawn segment's endpoint stretches it (the
    // grabbed endpoint and its weld cluster follow); a click without a drag
    // falls through on release as the owning edge select.
    if (hit.kind === 'looseVertex' && looseVertexDragController?.begin(hit)) {
        marqueeController?.cancel?.();
        return;
    }
    // Select-mode press-drag on a solid edge moves it (SketchUp's primary
    // edge interaction); a click without a drag falls through on release as
    // a chain select.
    if (hit.kind === 'solidEdge' && edgeDragController?.begin(hit)) {
        marqueeController?.cancel?.();
        return;
    }
    // Select-mode press-drag on a drawn segment moves it (endpoint weld
    // clusters and the pre-selection follow); a click without a drag falls
    // through on release as loose-edge select.
    if (hit.kind === 'edge' && looseEdgeDragController?.begin(hit)) {
        marqueeController?.cancel?.();
        return;
    }
    // Select-mode press-drag on an annotation repositions it (dimension dim
    // line / whole text label); a clean click is swallowed.
    if (hit.kind === 'annotation' && annotationDragController?.begin(hit)) {
        marqueeController?.cancel?.();
        return;
    }
    if (hit.kind === 'face') defaultFaceSelect(hit);
    else if (hit.kind === 'solidEdge') defaultSolidEdgeSelect(hit);
    else if (hit.kind === 'solidVertex') defaultSolidVertexSelect(hit);
    else if (hit.kind === 'edge' || hit.kind === 'looseVertex') defaultLooseEdgeSelect(hit);
    else {
        // An edit drag already owns the pointer (a second dispatch of the
        // same press, or a second pointer): this press must neither arm
        // the marquee nor run the deferred clear mid-drag.
        if (sketchDragOwnsPointer()) return;
        // Empty space: a press-drag becomes the window/crossing marquee. When
        // armed it owns the release (a clean click still clears, see the
        // controller); otherwise this stays the plain select/clear contract.
        if (marqueeController?.begin(hit)) return;
        // The gizmo's own pointerdown listener runs AFTER this dispatch, so
        // defer one tick — a press that starts a gizmo drag must not clear
        // the selection (a mouse hover flags the handle beforehand, but a
        // touch-first press does not).
        window.setTimeout(() => {
            if (gumballOwnsPointer()) return;
            defaultClear();
        }, 0);
    }
}

export function emitSketchMove(hit: SketchHit): void {
    if (gumballOwnsPointer()) return;
    if (!active && faceDragController?.dragging) {
        faceDragController.drag(hit);
        return;
    }
    if (!active && edgeDragController?.dragging) {
        edgeDragController.drag(hit);
        return;
    }
    if (!active && vertexDragController?.dragging) {
        vertexDragController.drag(hit);
        return;
    }
    if (!active && looseEdgeDragController?.dragging) {
        looseEdgeDragController.drag(hit);
        return;
    }
    if (!active && looseVertexDragController?.dragging) {
        looseVertexDragController.drag(hit);
        return;
    }
    if (!active && annotationDragController?.dragging) {
        annotationDragController.drag(hit);
        return;
    }
    active?.onMove?.(hit);
}

export function emitSketchKey(key: string, event: KeyboardEvent): void {
    active?.onKey?.(key, event);
}

/** Face-level default select helper for tools that consume a hit but want
 *  the standard selection applied too (Shift-add etc. live in the tools). */
export {
    defaultFaceSelect as sketchDefaultFaceSelect,
    defaultSolidEdgeSelect as sketchDefaultSolidEdgeSelect,
    defaultSolidVertexSelect as sketchDefaultSolidVertexSelect,
    defaultLooseEdgeSelect as sketchDefaultLooseEdgeSelect,
    defaultClear as sketchDefaultClear,
};
